import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { fetchAPI } from "../../config/api";
import { 
  processSprayChartData, 
  computeAggregates, 
  normalizeZone as normalizeZoneUtil,
  formatAvg as formatAvgUtil 
} from "../../utils/sprayChartUtils";

export const useSprayChartData = (playerId, initialYear, division, prefetchedData = null) => {
  const [playerData, setPlayerData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(!prefetchedData);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [error, setError] = useState(null);
  const [selectedZones, setSelectedZones] = useState([]);
  const [handFilter, setHandFilter] = useState({ L: true, R: true });
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const hasFetchedRef = useRef(false);
  const lastPlayerIdRef = useRef(playerId);

  useEffect(() => {
    hasFetchedRef.current = false;
    lastPlayerIdRef.current = playerId;
    setPlayerData(null);
    setEvents([]);
    setError(null);
    setSelectedZones([]);
    setHandFilter({ L: true, R: true });
    setAvailableYears([]);
    setSelectedYear(initialYear || null);
    setLoading(!prefetchedData);
    setIsTransitioning(false);
  }, [playerId, initialYear, prefetchedData]);

  useEffect(() => {
    if (prefetchedData) {
      const processed = processSprayChartData(prefetchedData);
      setPlayerData(processed);
      setEvents(Array.isArray(prefetchedData.events) ? prefetchedData.events : []);
      setAvailableYears(prefetchedData.available_years || []);
      setLoading(false);
      hasFetchedRef.current = true;
      return;
    }

    if (lastPlayerIdRef.current !== playerId) {
      hasFetchedRef.current = false;
      lastPlayerIdRef.current = playerId;
    }

    if (!selectedYear) return;

    const controller = new AbortController();

    (async () => {
      if (!hasFetchedRef.current) {
        setLoading(true);
      } else {
        setIsTransitioning(true);
      }
      
      try {
        const data = await fetchAPI(
          `/spraychart_data/${playerId}?year=${selectedYear}&division=${division}`,
          { signal: controller.signal }
        );
        
        if (!controller.signal.aborted) {
          const processed = processSprayChartData({ ...data, year: selectedYear });
          setPlayerData(processed);
          setEvents(Array.isArray(data.events) ? data.events : []);
          setAvailableYears(data.available_years || []);
          setError(null);
          hasFetchedRef.current = true;
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Error fetching player data:", e);
          setError(e.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setIsTransitioning(false);
        }
      }
    })();

    return () => controller.abort();
  }, [division, playerId, selectedYear, prefetchedData]);

  const aggregates = useMemo(() => {
    return computeAggregates(events, { handFilter, selectedZones });
  }, [events, handFilter, selectedZones]);

  const clearZones = useCallback(() => setSelectedZones([]), []);

  return {
    playerData,
    events,
    aggregates,
    loading,
    isTransitioning,
    error,
    selectedZones,
    setSelectedZones,
    handFilter,
    setHandFilter,
    clearZones,
    availableYears,
    selectedYear,
    setSelectedYear,
  };
};

export const formatAvg = formatAvgUtil;
export const normalizeZone = normalizeZoneUtil;
